/**
 * Square aspect ratio constant (1:1)
 */
export const SQUARE_ASPECT_RATIO = '1'

/**
 * Converts an aspect ratio string (e.g., '16:9') to a number
 * @param aspectRatio - Aspect ratio string in format 'width:height'
 * @returns The aspect ratio as a number (width / height)
 */
export const convertAspectRatioToNumber = (aspectRatio: string) => {
  const [width, height] = aspectRatio.split(':')
  return Number(width) / Number(height)
}

/**
 * Normalizes aspect ratio to ensure it's >= 1
 * If the aspect ratio is less than 1 (portrait), it defaults to SQUARE_ASPECT_RATIO
 * @param aspectRatio - Optional aspect ratio string (e.g., '16:9')
 * @returns Normalized aspect ratio string, defaults to SQUARE_ASPECT_RATIO if invalid or < 1
 */
export const normalizeAspectRatio = (aspectRatio?: string | null): string => {
  const ratio = aspectRatio || SQUARE_ASPECT_RATIO
  const ratioNumber = convertAspectRatioToNumber(ratio)

  return ratioNumber <= 1 ? SQUARE_ASPECT_RATIO : ratio
}

/**
 * Converts aspect ratio string format for CSS usage
 * Converts "16:9" format to "16/9" format for CSS aspect-ratio property
 * If already in "/" format or a number, returns as is
 * @param aspectRatio - Aspect ratio string (e.g., '16:9' or '16/9')
 * @returns CSS-compatible aspect ratio string
 */
export const convertAspectRatioForCSS = (aspectRatio: string): string => {
  // Convert "16:9" format to "16/9" format for CSS
  // If already in "/" format or a number, return as is
  if (aspectRatio.includes('/') || !aspectRatio.includes(':')) {
    return aspectRatio
  }
  return aspectRatio.replace(':', '/')
}

/**
 * Normalizes and converts aspect ratio for CSS usage
 * Combines normalization (ensures >= 1) and CSS conversion in one step
 * @param aspectRatio - Optional aspect ratio string (e.g., '16:9')
 * @returns CSS-compatible aspect ratio string, defaults to SQUARE_ASPECT_RATIO if invalid or < 1
 */
export const normalizeAndConvertAspectRatioForCSS = (
  aspectRatio?: string | null
): string => {
  const normalized = normalizeAspectRatio(aspectRatio)
  return convertAspectRatioForCSS(normalized)
}
